import { Button, Input, Link } from '@heroui/react';
import { Icon } from '@iconify/react';
import _ from 'lodash';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  EducationTree,
  filteredEducationTree,
  treeSortEducation,
} from '@/patient-app-common/helpers/education_helpers';
import { Education } from '@/patient-app-common/types/Education';

interface EduListProps {
  education: Education[];
  hasAiChatbot: boolean;
  topicId: string | undefined;
  setSidebarOverlaps: (boolean: boolean) => void;
}

export function EduList({
  education,
  hasAiChatbot,
  topicId,
  setSidebarOverlaps,
}: EduListProps) {
  const { t } = useTranslation('education');
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');

  const filterBySearch = (edu: Education[]) => {
    const term = searchTerm.toLowerCase();
    return edu.filter(
      (topic) =>
        topic.title.toLowerCase().includes(term) ||
        topic.html.toLowerCase().includes(term)
    );
  };

  const availableTopics = useMemo(() => {
    if (!education.length) return [];

    if (searchTerm) {
      // get all IDs of titles that match the search term
      const searchedIds = _.map(filterBySearch(education), 'id');
      // include all children that belong to the matched titles
      const topicsTree = filteredEducationTree(education, searchedIds);
      return topicsTree;
    }

    const topicsTree = treeSortEducation(education, null);
    return topicsTree;
  }, [education, searchTerm]);

  const getListTree = (components: EducationTree[]) => {
    const list = components.map((component) => {
      const isSelected = topicId === component.id.toString();
      const isParent = component.html === '' || !component.parentId;

      let subtopics: EducationTree[] = [];

      if (component.children?.length) {
        subtopics = [...component.children];
      }

      return (
        <div key={component.id} role="listitem">
          <div
            className={`
              p-0.5
              ${isParent && 'font-semibold'}
              ${isSelected && 'bg-primary-100'}`}
          >
            {!component.html ? (
              <p className="text-default-700">{component.title}</p>
            ) : (
              <Link key={component.id} href={`/education/${component.id}`}>
                {component.title}
              </Link>
            )}
          </div>
          {!_.isEmpty(subtopics) ? (
            <div role="listitem" className="pl-4">
              {getListTree(subtopics)}
            </div>
          ) : null}
        </div>
      );
    });
    return list;
  };

  return (
    <div className="py-8 px-4 space-y-4">
      <Input
        isClearable
        label={t('edulist.searchbar')}
        onChange={(e) => setSearchTerm(e.target.value)}
        onClear={() => setSearchTerm('')}
        placeholder={t('edulist.searchbar')}
        startContent={<Icon icon="mdi:search" />}
      />
      {hasAiChatbot && (
        <Button
          color="primary"
          className="w-full bg-primary-500 text-white border-0 rounded-xl text-base font-[900] cursor-pointer flex items-center justify-center gap-2.5 mb-5"
          onPress={() => {
            navigate('/education');
            setSidebarOverlaps(false);
          }}
          size="lg"
          startContent={<Icon icon="mdi:star-four-points-outline" />}
          variant="flat"
        >
          {t('common.link.answers')}
        </Button>
      )}
      <section role="list">{getListTree(availableTopics)}</section>
      {!_.isEmpty(searchTerm) && _.isEmpty(availableTopics) && (
        <>{t('edulist.search_empty', { searchTerm })}</>
      )}
      {!_.isEmpty(searchTerm) && (
        <div className="flex justify-center">
          <Button
            color="primary"
            className="w-full"
            onPress={() => setSearchTerm('')}
            variant="faded"
          >
            {t('edulink.button.index')}
          </Button>
        </div>
      )}
    </div>
  );
}
